/**
 * PIP-408 — Refine admin venue-candidate schemas.
 *
 * While the venue moratorium is on, ingestion lanes never mint a `Location`:
 * an unmatched raw venue is stored as a venue candidate and its events wait,
 * QUARANTINED, until an admin promotes the candidate to a real venue or
 * attaches it to an existing one. These schemas validate the three admin
 * routes under `/api/v1/admin/venue-candidates`.
 */
import { z } from 'zod';

const latSchema = z.number().min(-90).max(90);
const lngSchema = z.number().min(-180).max(180);
const optionalText = (max: number) => z.string().trim().min(1).max(max).optional();

/** `GET /api/v1/admin/venue-candidates` — `pending` (default), `resolved`, or `all`. */
export const ADMIN_VENUE_CANDIDATE_STATUSES = ['pending', 'resolved', 'all'] as const;
export type AdminVenueCandidateStatus = (typeof ADMIN_VENUE_CANDIDATE_STATUSES)[number];

export const adminVenueCandidatesListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    status: z.enum(ADMIN_VENUE_CANDIDATE_STATUSES).default('pending'),
    /** Case-insensitive substring of the raw venue name. */
    q: z.string().trim().min(1).max(200).optional(),
  })
  .strict();
export type AdminVenueCandidatesListQuery = z.infer<typeof adminVenueCandidatesListQuerySchema>;

/** `:id` path segment (a cuid, or a raw-SQL `vc_` id from the standalone script). */
export const adminVenueCandidateIdParamSchema = z.string().trim().min(1).max(128);

/**
 * `POST /api/v1/admin/venue-candidates/:id/promote` — optional corrections to
 * the raw venue. Coordinates come from `lat`/`lng`, else the candidate's own
 * feed coordinates, else geocoding the (corrected) address.
 */
export const adminPromoteVenueCandidateSchema = z
  .object({
    name: optionalText(200),
    address: optionalText(500),
    city: optionalText(200),
    state: optionalText(100),
    postalCode: optionalText(20),
    lat: latSchema.optional(),
    lng: lngSchema.optional(),
  })
  .strict()
  .refine((body) => (body.lat === undefined) === (body.lng === undefined), {
    message: 'lat and lng must be supplied together',
    path: ['lat'],
  });
export type AdminPromoteVenueCandidateBody = z.infer<typeof adminPromoteVenueCandidateSchema>;

/** `POST /api/v1/admin/venue-candidates/:id/attach` — the existing venue. */
export const adminAttachVenueCandidateSchema = z
  .object({
    locationId: z.string().trim().min(1).max(128),
  })
  .strict();
export type AdminAttachVenueCandidateBody = z.infer<typeof adminAttachVenueCandidateSchema>;
